import { PageTemplate, Region, Suburb } from '../../../../models/content';
import { globalBlockTokens } from '../../global-blocks';
import { contentSlug } from '../../../../content-slug';
import { regionHubPath } from './suburb-content';

/**
 * A suburb's website page (#253, #262): built by the site from the Suburb template (a sub-region's from the Sub-region
 * template), filled from the suburb's record, plus the blocks inserted on that page only. It lives at the suburb's
 * funeral-services address and is the suburb's only page (#257): the site 301s the live site's funeral-directors,
 * direct-cremation and memorial-services addresses to it. No CMS page is written for it.
 */

/** The suburb's page address: /{state}/{region}/funeral-services/{slug}. */
export function suburbPagePath(region: Region, slug: string): string {
  return `${regionHubPath(region)}/funeral-services/${slug}`;
}

/**
 * The publish-scope id of a page, mirroring picaluna-v3's pageSlugFromPath (and the Pages list's pageSlug):
 * "/nsw/sydney/funeral-services/randwick" → "nsw-sydney-funeral-services-randwick".
 */
export function pageScopeId(path: string): string {
  const trimmed = (path || '').replace(/^\/+|\/+$/g, '');
  return trimmed === '' ? 'home' : contentSlug(trimmed.replace(/\//g, '-'));
}

/**
 * What a Publish or Un-publish of this suburb ships: its suburbs.json row, and the pages token of its address, which
 * takes a page designed before the template (converted since) out of the site's snapshot and is a no-op otherwise.
 * The global blocks its own blocks place ride along (#217), so a newly placed one shows on the preview.
 */
export function suburbPublishScope(
  region: Region,
  suburb: Pick<Suburb, 'slug' | 'redirectFrom'> & Partial<Pick<Suburb, 'blocks'>>
): string {
  const tokens = new Set([
    `suburbs:${region.slug}--${suburb.slug}`,
    `pages:${pageScopeId(suburbPagePath(region, suburb.slug))}`,
  ]);
  // A rename ships both halves: the old suburb row comes off in the same publish (the sync drops what's in scope but
  // no longer in Conductor), so the old and new addresses are never both live without a 301.
  for (const old of suburb.redirectFrom ?? []) tokens.add(`suburbs:${region.slug}--${old}`);
  for (const t of globalBlockTokens(suburb.blocks)) tokens.add(t);
  return [...tokens].join(' ');
}

/** What a template's Publish ships: every published page built from it (#262), and the global blocks it places (#217). */
export function templatePublishScope(key: 'suburb' | 'subregion', template?: Pick<PageTemplate, 'blocks'> | null): string {
  return [`pageTemplates:${key}`, ...globalBlockTokens(template?.blocks)].join(' ');
}

/**
 * The two sub-region building blocks (#260): 'subregions-list' (the sub-regions of a region) and 'suburb-subregions'
 * (the sub-regions a suburb is part of). picaluna-v3 aborts the whole build on a section component it doesn't know, so
 * neither may be offered in the editor before the site accepts both keys (plan §6.5 V3, content.config.ts
 * SECTION_COMPONENTS). picaluna-v3 feature/suburb-content accepts them from b82a674: release that first, or set this to
 * false before releasing the client without it.
 */
export const SUBREGION_BLOCKS_LIVE = true;

/**
 * The suburb template's two data blocks (#262): 'suburb-arrangers' (arrangers who work here) and 'suburb-families'
 * (families helped). The same rule as SUBREGION_BLOCKS_LIVE: picaluna-v3 feature/suburb-content accepts them with #262,
 * and the release order is v3, then the server, then this client. Set it to false to release the client first.
 */
export const SUBURB_TEMPLATE_BLOCKS_LIVE = true;

/**
 * 'suburb-map' (Ivan 2026-11-03): one Google map of the page's places, a Suburb / Sub-region template block. The same
 * rule: picaluna-v3 feature/place-cards accepts it, and the release order is v3, then the server, then this client.
 * Set it to false to release the client first.
 */
export const SUBURB_MAP_BLOCK_LIVE = true;
